import { t } from 'i18next';
import z from 'zod';

import { Environment } from '../models';

import { EnvironmentUtils } from './environment.utils';

const ERROR_NAME = 'AnyXError';

/**
 * Environments where the console write is allowed.
 *
 * This is an ALLOWLIST on purpose. `Environment` is a numeric enum declared
 * `{ PRODUCTION, STAGING, PREVIEW, LOCAL }`, so `Environment.PRODUCTION === 0`
 * and any truthiness test (`if (!env)`, `env || fallback`) would invert the
 * gate. A denylist would also leak in production if the value were ever
 * `undefined` before the store is hydrated. With an allowlist, anything that is
 * not explicitly one of these — production, or an unhydrated value — is a
 * silent no-op.
 */
export const CONSOLE_REPORTING_ENVIRONMENTS: readonly Environment[] = [
  Environment.LOCAL,
  Environment.PREVIEW,
  Environment.STAGING,
];

/**
 * Single reporting seam for runtime errors that would otherwise be swallowed.
 *
 * **In production this is deliberately a no-op.** Sentry sent captured errors
 * server-side and never echoed them to the page, so writing them to the console
 * instead would be a new client-visible exposure: a caught Apollo error carries
 * query text, variables, server-side messages, internal field names and a stack
 * trace, and the production console of a merchant-facing app is a user-facing
 * surface (see `.claude/rules/observability.md` and `.claude/rules/security.md`).
 * Outside production the console write is the fastest useful signal, so it stays.
 *
 * That means production behaviour today is identical to having deleted these
 * calls — which is the honest state until AnyMindGroup/anyx#1603 wires up a real
 * provider. The function exists so that is a one-file change rather than
 * re-instrumenting ~120 call sites.
 *
 * The environment is read per call, not at module load: the store is hydrated at
 * app startup and a module-level read could capture a pre-hydration value.
 *
 * @param error the thrown value, or a descriptive string for non-throw failures
 * @param context optional structured detail to log alongside the error
 */
export function reportAppError(error: unknown, context?: Record<string, unknown>): void {
  if (!CONSOLE_REPORTING_ENVIRONMENTS.includes(EnvironmentUtils.getCurrentEnvironment())) {
    return;
  }

  if (context) {
    console.error(error, context);

    return;
  }

  console.error(error);
}

export type ErrorMap<ErrorCode extends string> = {
  readonly [K in ErrorCode]: string;
};

export interface ErrorData {
  [key: string]: unknown;
}

export class AnyXError extends Error {
  /** The custom name for all AnyXError. */
  override readonly name: string = ERROR_NAME;

  constructor(
    /** The error code for this error. */
    readonly code: string,
    /** The error message for this error. */
    message?: string,
    /** Custom data for this error. */
    public customData?: Record<string, unknown>
  ) {
    super(message || code);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AnyXError);
    }
  }
}

export class ErrorFactory<
  ErrorCode extends string,
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  ErrorParams extends { readonly [K in ErrorCode]?: ErrorData } = {},
> {
  constructor(
    /** i18n namespace */
    private readonly namespace: string,
    private readonly errors: Partial<ErrorMap<ErrorCode>>,
    private readonly fallbackError?: string
  ) { }

  create<K extends ErrorCode>(
    code: K,
    ...data: K extends keyof ErrorParams ? [ErrorParams[K]] : []
  ): AnyXError {
    const customData = (data[0] as ErrorData) || {};
    const key = this.errors[code];

    const translatedMessage = key ? translateKey(key, this.namespace, customData) : code;
    const errorMessage = this.fallbackError ?? code;

    const message = translatedMessage === key ? errorMessage : translatedMessage;

    return new AnyXError(code, message, customData);
  }
}

const i18nOptionsSchema = z.object({
  i18nOptions: z.record(z.any()),
});

function translateKey(key: string, ns: string, data: ErrorData): string {
  try {
    const options = i18nOptionsSchema.parse(data);

    return t(key, { ns, ...options.i18nOptions });
  } catch {
    return t(key, { ns });
  }
}
